import React, { useEffect, useMemo, useState } from 'react'

import { useRouteMatch } from 'react-router-dom'

import AuthGuestContext from 'contexts/AuthGuestContext'
import * as service from 'service'

const HOME_ROUTES_BY_MODULE = {
  privacy_by_design: '/guests/privacy-by-design/list',
  ai_inventory: '/guests/ai-inventory/list',
}

const JOURNEY_PATHS = [
  '/:organizationSlug/guests/:portalToken/privacy-by-design',
  '/:organizationSlug/guests/:portalToken/ai-inventory',
]

const AuthGuestProvider = ({ children }) => {
  const slugMatch = useRouteMatch('/:organizationSlug/guests')
  const urlOrganizationSlug = slugMatch?.params?.organizationSlug || 'dponet'

  const journeyMatch = useRouteMatch(JOURNEY_PATHS)
  const urlPortalToken = journeyMatch?.params?.portalToken

  const [user, setUser] = useState(null)
  const [companyId, setCompanyId] = useState(null)
  const [guestModule, setGuestModule] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const [portalToken, setPortalToken] = useState(
    urlPortalToken || service.guestPortal.auth.getPortalToken() || null,
  )
  const [company, setCompany] = useState(null)
  const [linkInvalid, setLinkInvalid] = useState(false)

  const organizationSlug = company?.organizationSlug || urlOrganizationSlug

  const homeRoute = useMemo(() => {
    if (!guestModule) return null
    const baseRoute = HOME_ROUTES_BY_MODULE[guestModule]
    return baseRoute ? `/${organizationSlug}${baseRoute}` : null
  }, [guestModule, organizationSlug])

  const loadCompany = async (token) => {
    if (!token) return null
    const response = await service.guestPortal.company.getCompanyInfo(token)
    const loadedCompany = response?.data?.company || null
    setCompany(loadedCompany)
    setPortalToken(token)
    service.guestPortal.auth.setPortalToken(token)
    return loadedCompany
  }

  const loadOut = () => {
    setUser(null)
    setCompanyId(null)
    setGuestModule(null)
    setCompany(null)
    setPortalToken(null)
    setLinkInvalid(false)
    setLoaded(true)
  }

  useEffect(() => {
    const bootstrap = async () => {
      if (service.guestPortal.auth.isAuthenticated()) {
        const token = service.guestPortal.auth.getToken()
        const payload = service.guestPortal.auth.decodeToken(token)

        if (payload) {
          setCompanyId(payload.companyId)
          setGuestModule(payload.guestModule)
          try {
            const profileResponse = await service.guestPortal.profile.get()
            const guestUser = profileResponse?.data?.guestUser
            setUser({
              id: guestUser?.id,
              responsibleName: guestUser?.responsibleName || null,
              departmentId: guestUser?.departmentId || null,
            })
          } catch {
            service.guestPortal.auth.removeToken()
          }
        } else {
          service.guestPortal.auth.removeToken()
        }
      }

      const tokenToLoad =
        urlPortalToken || service.guestPortal.auth.getPortalToken()

      if (tokenToLoad) {
        try {
          const loadedCompany = await loadCompany(tokenToLoad)

          const canonicalSlug = loadedCompany?.organizationSlug
          if (canonicalSlug && canonicalSlug !== urlOrganizationSlug) {
            const correctedPath = service.history.location.pathname.replace(
              `/${urlOrganizationSlug}/guests`,
              `/${canonicalSlug}/guests`,
            )
            service.history.replace(correctedPath)
          }
        } catch (error) {
          if (error?.response?.status === 404) {
            setLinkInvalid(true)
          }
          service.guestPortal.auth.removePortalToken()
          setPortalToken(null)
          setCompany(null)
        }
      }

      setLoaded(true)
    }

    bootstrap()
    // eslint-disable-next-line
  }, [urlPortalToken])

  return (
    <AuthGuestContext.Provider
      value={{
        company,
        companyId,
        guestModule,
        homeRoute,
        linkInvalid,
        loadCompany,
        loadOut,
        loaded,
        organizationSlug,
        portalToken,
        setUser,
        user,
      }}
    >
      {children}
    </AuthGuestContext.Provider>
  )
}

export default AuthGuestProvider
